import React, { useState, useEffect, useCallback } from 'react';

// Industry data for research
import { industryData } from '../../data/industryDataComplete';
import industryRPE from '../../data/industryRPE';

/**
 * BenchmarkManager - Admin component for researching and updating industry benchmarks
 * Features:
 * - AI-powered research (OpenAI + Perplexity fallback)
 * - Staging area for review before commit
 * - Version history with rollback
 * - Side-by-side comparison of current vs proposed values
 */
export default function BenchmarkManager() {
  const [benchmarkType, setBenchmarkType] = useState('RPE'); // 'RPE' | 'WASTE' | 'AUTOMATION'
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [staging, setStaging] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [researching, setResearching] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [generatedCode, setGeneratedCode] = useState(null);
  const [expandedIndustry, setExpandedIndustry] = useState(null);

  // Get auth token
  const getToken = () => sessionStorage.getItem('adminToken') || localStorage.getItem('adminToken') || '';

  // Flatten industries with their current values
  const industriesWithData = React.useMemo(() => {
    const result = [];
    
    // Get RPE data by industry ID
    const rpeById = {};
    Object.entries(industryRPE).forEach(([category, data]) => {
      (data.industries || []).forEach(ind => {
        rpeById[ind.id] = { ...ind, category };
      });
    });

    // Merge with industry data
    industryData.forEach(ind => {
      const rpe = rpeById[ind.id] || {};
      result.push({
        id: ind.id,
        name: ind.name,
        category: ind.category,
        // Waste data from industryDataComplete
        wasteLow: ind.wasteLow,
        wasteHigh: ind.wasteHigh,
        // RPE data from industryRPE
        USrpeLow: rpe.USrpeLow,
        USrpeHigh: rpe.USrpeHigh,
        BRrpeLow: rpe.BRrpeLow,
        BRrpeHigh: rpe.BRrpeHigh,
        LArpeLow: rpe.LArpeLow,
        LArpeHigh: rpe.LArpeHigh
      });
    });

    return result;
  }, []);

  // Group industries by category
  const industriesByCategory = React.useMemo(() => {
    return industriesWithData.reduce((acc, ind) => {
      if (!acc[ind.category]) acc[ind.category] = [];
      acc[ind.category].push(ind);
      return acc;
    }, {});
  }, [industriesWithData]);

  // Load staging and history on mount
  const loadStagingData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      
      // Get staging
      const stagingRes = await fetch('/.netlify/functions/benchmark-staging', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-staging' })
      });
      const stagingData = await stagingRes.json();
      if (stagingData.ok) setStaging(stagingData.staging);

      // Get history
      const historyRes = await fetch('/.netlify/functions/benchmark-staging', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-history' })
      });
      const historyData = await historyRes.json();
      if (historyData.ok) setHistory(historyData.history || []);

    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to load staging data: ' + e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStagingData();
  }, [loadStagingData]);

  // Toggle industry selection
  const toggleIndustry = (id) => {
    setSelectedIndustries(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Select all in category
  const selectCategory = (category) => {
    const ids = industriesByCategory[category].map(i => i.id);
    setSelectedIndustries(prev => {
      const allSelected = ids.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !ids.includes(id));
      } else {
        return [...new Set([...prev, ...ids])];
      }
    });
  };

  // Research selected industries
  const researchSelected = async () => {
    if (selectedIndustries.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one industry' });
      return;
    }

    if (selectedIndustries.length > 10) {
      setMessage({ type: 'error', text: 'Please select 10 or fewer industries at a time to avoid rate limits' });
      return;
    }

    setResearching(true);
    setMessage({ type: 'info', text: `Researching ${selectedIndustries.length} industries...` });

    try {
      const token = getToken();
      
      // Build industry objects with current values
      const industries = selectedIndustries.map(id => {
        const ind = industriesWithData.find(i => i.id === id);
        return {
          id: ind.id,
          name: ind.name,
          category: ind.category,
          current: benchmarkType === 'RPE' 
            ? { USrpeLow: ind.USrpeLow, USrpeHigh: ind.USrpeHigh, BRrpeLow: ind.BRrpeLow, BRrpeHigh: ind.BRrpeHigh, LArpeLow: ind.LArpeLow, LArpeHigh: ind.LArpeHigh }
            : { wasteLow: ind.wasteLow, wasteHigh: ind.wasteHigh }
        };
      });

      const res = await fetch('/.netlify/functions/benchmark-research', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'research', industries, benchmarkType })
      });

      const data = await res.json();
      
      if (!data.ok) {
        throw new Error(data.error || 'Research failed');
      }

      // Stage the results
      const stageRes = await fetch('/.netlify/functions/benchmark-staging', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'stage', changes: data.results, benchmarkType })
      });

      const stageData = await stageRes.json();
      
      if (stageData.ok) {
        setMessage({ type: 'success', text: `Researched and staged ${data.results.length} industries` });
        await loadStagingData();
        setSelectedIndustries([]);
      } else {
        throw new Error(stageData.error || 'Failed to stage results');
      }

    } catch (e) {
      setMessage({ type: 'error', text: 'Research failed: ' + e.message });
    } finally {
      setResearching(false);
    }
  };

  // Update a staged value manually
  const updateStagedValue = async (industryId, field, value) => {
    try {
      const token = getToken();
      const res = await fetch('/.netlify/functions/benchmark-staging', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update-staged', industryId, field, value: parseFloat(value), benchmarkType })
      });
      const data = await res.json();
      if (data.ok) {
        await loadStagingData();
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to update: ' + e.message });
    }
  };

  // Commit staged changes
  const commitChanges = async () => {
    if (!staging || !staging.changes || staging.changes.length === 0) {
      setMessage({ type: 'error', text: 'No staged changes to commit' });
      return;
    }

    if (!window.confirm(`Are you sure you want to commit ${staging.changes.length} changes? This will generate code for you to apply.`)) {
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch('/.netlify/functions/benchmark-staging', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'commit', benchmarkType: staging.benchmarkType })
      });

      const data = await res.json();
      
      if (data.ok) {
        setMessage({ type: 'success', text: 'Changes committed! Copy the generated code below.' });
        setGeneratedCode(data);
        await loadStagingData();
      } else {
        throw new Error(data.error || 'Commit failed');
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Commit failed: ' + e.message });
    } finally {
      setLoading(false);
    }
  };

  // Discard staging
  const discardStaging = async () => {
    if (!window.confirm('Discard all staged changes?')) return;

    try {
      const token = getToken();
      const res = await fetch('/.netlify/functions/benchmark-staging', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'discard' })
      });
      const data = await res.json();
      if (data.ok) {
        setMessage({ type: 'success', text: 'Staging discarded' });
        setStaging(null);
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to discard: ' + e.message });
    }
  };

  // Rollback to version
  const rollbackToVersion = async (versionId) => {
    if (!window.confirm(`Rollback to version ${versionId}? This will stage the previous values for review.`)) return;

    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch('/.netlify/functions/benchmark-staging', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'rollback', versionId })
      });
      const data = await res.json();
      if (data.ok) {
        setMessage({ type: 'success', text: `Rollback staged. Review and commit to apply.` });
        await loadStagingData();
      } else {
        throw new Error(data.error || 'Rollback failed');
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Rollback failed: ' + e.message });
    } finally {
      setLoading(false);
    }
  };

  // Format currency for display
  const formatCurrency = (val, currency = 'USD') => {
    if (val === undefined || val === null) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(val);
  };

  // Format percentage
  const formatPercent = (val) => {
    if (val === undefined || val === null) return '-';
    return `${(val * 100).toFixed(1)}%`;
  };

  // Calculate change percentage
  const calcChange = (current, proposed) => {
    if (!current || !proposed) return null;
    const change = ((proposed - current) / current) * 100;
    return change;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Benchmark Manager</h2>
        <div className="flex gap-2">
          <select 
            value={benchmarkType} 
            onChange={e => setBenchmarkType(e.target.value)}
            className="border rounded px-3 py-1 text-sm"
          >
            <option value="RPE">Revenue Per Employee</option>
            <option value="WASTE">Process Waste</option>
            <option value="AUTOMATION">Automation Potential</option>
          </select>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-3 rounded text-sm ${
          message.type === 'error' ? 'bg-red-100 text-red-700' :
          message.type === 'success' ? 'bg-green-100 text-green-700' :
          'bg-blue-100 text-blue-700'
        }`}>
          {message.text}
          <button onClick={() => setMessage({ type: '', text: '' })} className="ml-2 font-bold">×</button>
        </div>
      )}

      {/* Staging Area */}
      {staging && staging.changes && staging.changes.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-yellow-800">
              📋 Staged Changes ({staging.changes.length})
              {staging.isRollback && <span className="ml-2 text-xs bg-yellow-200 px-2 py-0.5 rounded">Rollback</span>}
            </h3>
            <div className="flex gap-2">
              <button 
                onClick={discardStaging}
                className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
              >
                Discard
              </button>
              <button 
                onClick={commitChanges}
                disabled={loading}
                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Committing...' : 'Commit Changes'}
              </button>
            </div>
          </div>

          {/* Staged items comparison table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-yellow-200">
                  <th className="text-left py-2 px-2">Industry</th>
                  {benchmarkType === 'RPE' ? (
                    <>
                      <th className="text-right py-2 px-2">Current US Low</th>
                      <th className="text-right py-2 px-2">Proposed US Low</th>
                      <th className="text-right py-2 px-2">Current US High</th>
                      <th className="text-right py-2 px-2">Proposed US High</th>
                      <th className="text-center py-2 px-2">Confidence</th>
                    </>
                  ) : (
                    <>
                      <th className="text-right py-2 px-2">Current Low</th>
                      <th className="text-right py-2 px-2">Proposed Low</th>
                      <th className="text-right py-2 px-2">Current High</th>
                      <th className="text-right py-2 px-2">Proposed High</th>
                      <th className="text-center py-2 px-2">Confidence</th>
                    </>
                  )}
                  <th className="text-center py-2 px-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {staging.changes.map(change => {
                  const isExpanded = expandedIndustry === change.industryId;
                  return (
                    <React.Fragment key={change.industryId}>
                      <tr 
                        className="border-b border-yellow-100 hover:bg-yellow-100 cursor-pointer"
                        onClick={() => setExpandedIndustry(isExpanded ? null : change.industryId)}
                      >
                        <td className="py-2 px-2 font-medium">
                          <span className="mr-1">{isExpanded ? '▼' : '▶'}</span>
                          {change.industryName}
                        </td>
                        {benchmarkType === 'RPE' ? (
                          <>
                            <td className="text-right py-2 px-2 text-gray-500">{formatCurrency(change.current?.USrpeLow)}</td>
                            <td className="text-right py-2 px-2">
                              <input 
                                type="number" 
                                value={change.researched?.USrpeLow || ''} 
                                onChange={e => updateStagedValue(change.industryId, 'USrpeLow', e.target.value)}
                                onClick={e => e.stopPropagation()}
                                className="w-24 border rounded px-1 py-0.5 text-right"
                              />
                              {calcChange(change.current?.USrpeLow, change.researched?.USrpeLow) !== null && (
                                <span className={`ml-1 text-xs ${calcChange(change.current?.USrpeLow, change.researched?.USrpeLow) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {calcChange(change.current?.USrpeLow, change.researched?.USrpeLow) > 0 ? '+' : ''}{calcChange(change.current?.USrpeLow, change.researched?.USrpeLow).toFixed(1)}%
                                </span>
                              )}
                            </td>
                            <td className="text-right py-2 px-2 text-gray-500">{formatCurrency(change.current?.USrpeHigh)}</td>
                            <td className="text-right py-2 px-2">
                              <input 
                                type="number" 
                                value={change.researched?.USrpeHigh || ''} 
                                onChange={e => updateStagedValue(change.industryId, 'USrpeHigh', e.target.value)}
                                onClick={e => e.stopPropagation()}
                                className="w-24 border rounded px-1 py-0.5 text-right"
                              />
                            </td>
                            <td className="text-center py-2 px-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                change.researched?.confidence === 'high' ? 'bg-green-100 text-green-700' :
                                change.researched?.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {change.researched?.confidence || '-'}
                              </span>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="text-right py-2 px-2 text-gray-500">{formatPercent(change.current?.wasteLow)}</td>
                            <td className="text-right py-2 px-2">
                              <input 
                                type="number" 
                                step="0.01"
                                value={change.researched?.wasteLow || ''} 
                                onChange={e => updateStagedValue(change.industryId, 'wasteLow', e.target.value)}
                                onClick={e => e.stopPropagation()}
                                className="w-20 border rounded px-1 py-0.5 text-right"
                              />
                            </td>
                            <td className="text-right py-2 px-2 text-gray-500">{formatPercent(change.current?.wasteHigh)}</td>
                            <td className="text-right py-2 px-2">
                              <input 
                                type="number" 
                                step="0.01"
                                value={change.researched?.wasteHigh || ''} 
                                onChange={e => updateStagedValue(change.industryId, 'wasteHigh', e.target.value)}
                                onClick={e => e.stopPropagation()}
                                className="w-20 border rounded px-1 py-0.5 text-right"
                              />
                            </td>
                            <td className="text-center py-2 px-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                change.researched?.confidence === 'high' ? 'bg-green-100 text-green-700' :
                                change.researched?.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {change.researched?.confidence || '-'}
                              </span>
                            </td>
                          </>
                        )}
                        <td className="text-center py-2 px-2 text-xs text-gray-500">
                          {change.source || change.researched?.source || '-'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-yellow-50">
                          <td colSpan={7} className="p-3">
                            <div className="text-xs space-y-1">
                              {change.researched?.sources && (
                                <p><strong>Sources:</strong> {change.researched.sources.join(', ')}</p>
                              )}
                              {change.researched?.notes && (
                                <p><strong>Notes:</strong> {change.researched.notes}</p>
                              )}
                              {change.researched?.dataYear && (
                                <p><strong>Data Year:</strong> {change.researched.dataYear}</p>
                              )}
                              {benchmarkType === 'RPE' && (
                                <div className="mt-2 grid grid-cols-3 gap-2">
                                  <div>
                                    <strong>Brazil (BRL):</strong><br/>
                                    Low: {formatCurrency(change.researched?.BRrpeLow, 'BRL')}<br/>
                                    High: {formatCurrency(change.researched?.BRrpeHigh, 'BRL')}
                                  </div>
                                  <div>
                                    <strong>Latin America (USD-PPP):</strong><br/>
                                    Low: {formatCurrency(change.researched?.LArpeLow)}<br/>
                                    High: {formatCurrency(change.researched?.LArpeHigh)}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Generated Code Output */}
      {generatedCode && (
        <div className="bg-gray-900 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold text-white">Generated Code - {generatedCode.affectedFile}</h3>
            <button 
              onClick={() => {
                navigator.clipboard.writeText(generatedCode.generatedCode);
                setMessage({ type: 'success', text: 'Code copied to clipboard!' });
              }}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Copy Code
            </button>
          </div>
          <pre className="text-green-400 text-xs overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto">
            {generatedCode.generatedCode}
          </pre>
          <p className="text-yellow-400 text-xs mt-2">
            ⚠️ {generatedCode.instructions}
          </p>
          <button 
            onClick={() => setGeneratedCode(null)}
            className="mt-2 px-3 py-1 text-sm bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Industry Selection */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-gray-800">Select Industries to Research</h3>
          <div className="flex gap-2">
            <span className="text-sm text-gray-500">{selectedIndustries.length} selected</span>
            <button 
              onClick={() => setSelectedIndustries([])}
              className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
            >
              Clear
            </button>
            <button 
              onClick={researchSelected}
              disabled={researching || selectedIndustries.length === 0}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {researching ? 'Researching...' : `Research Selected (${selectedIndustries.length})`}
            </button>
          </div>
        </div>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {Object.entries(industriesByCategory).map(([category, industries]) => (
            <div key={category} className="border rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <input 
                  type="checkbox"
                  checked={industries.every(i => selectedIndustries.includes(i.id))}
                  onChange={() => selectCategory(category)}
                  className="rounded"
                />
                <span className="font-medium text-gray-700 capitalize">{category}</span>
                <span className="text-xs text-gray-500">({industries.length})</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 ml-6">
                {industries.map(ind => (
                  <label key={ind.id} className="flex items-center gap-1 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input 
                      type="checkbox"
                      checked={selectedIndustries.includes(ind.id)}
                      onChange={() => toggleIndustry(ind.id)}
                      className="rounded"
                    />
                    <span className="truncate">{ind.name}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Version History */}
      {history.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Version History</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {history.map(version => (
              <div key={version.id} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                <div>
                  <span className="font-medium">{version.id}</span>
                  <span className="text-gray-500 ml-2">
                    {new Date(version.committedAt).toLocaleString()}
                  </span>
                  <span className="text-gray-500 ml-2">
                    ({version.changesCount} changes, {version.benchmarkType})
                  </span>
                </div>
                <button 
                  onClick={() => rollbackToVersion(version.id)}
                  className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                >
                  Rollback
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help */}
      <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
        <h4 className="font-semibold mb-2">How to use:</h4>
        <ol className="list-decimal ml-4 space-y-1">
          <li>Select the benchmark type (RPE, Waste, or Automation)</li>
          <li>Select industries to research (max 10 at a time)</li>
          <li>Click "Research Selected" - AI will search for current benchmarks</li>
          <li>Review staged changes - edit values if needed</li>
          <li>Click "Commit Changes" to generate update code</li>
          <li>Copy the generated code and update the source file</li>
          <li>Redeploy to apply changes</li>
        </ol>
        <p className="mt-2 text-xs">
          <strong>Note:</strong> Uses OpenAI (primary) with Perplexity fallback for real-time web search.
          Requires OPENAI_API_KEY and/or PERPLEXITY_API_KEY environment variables.
        </p>
      </div>
    </div>
  );
}
